import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { trpc } from '@/lib/trpc';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  Heading,
  Link as KibbleLink,
  Spinner,
  Text,
  TextInput,
} from '@televet/kibble-ui';
import { Center, Flex, Stack } from '@televet/kibble-ui/chakra';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/providers/auth';
import { useEffect } from 'react';

const formSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be less than 50 characters'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be less than 50 characters'),
});

type FormData = z.infer<typeof formSchema>;

export const Route = createFileRoute('/accept-invitation/$code')({
  component: RouteComponent,
});

function RouteComponent() {
  const { code } = useParams({ from: '/accept-invitation/$code' });
  const navigate = useNavigate();
  const { isAuthenticated, logout, loginWithCustomToken } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
    },
  });

  const { data: invitation, isPending: isFetchingInvitation } = useQuery(
    trpc.clinic.getTeamInvitation.queryOptions({ code })
  );

  const {
    mutateAsync: acceptTeamInvitation,
    isIdle: isAcceptingTeamInvitationIdle,
    isPending: isAcceptingTeamInvitation,
  } = useMutation(trpc.clinic.acceptTeamInvitation.mutationOptions());

  useEffect(() => {
    if (isAuthenticated && invitation && isAcceptingTeamInvitationIdle) {
      logout();
    }
  }, [invitation, isAcceptingTeamInvitationIdle, isAuthenticated, logout]);

  const onSubmit = async (data: FormData) => {
    const { loginToken } = await acceptTeamInvitation({
      code,
      firstName: data.firstName,
      lastName: data.lastName,
    });
    await loginWithCustomToken({ token: loginToken });
    navigate({ to: '/' });
  };

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      h="100dvh"
      w="100dvw"
      bg="tertiary.50"
      p={12}
    >
      <Card maxW="lg" position="relative" top="-140px" px={4} py={2}>
        {!invitation ? (
          <Center>
            <Spinner />
          </Center>
        ) : invitation?.acceptedAt ? (
          <Flex direction="column" gap={2}>
            <Text variant="subtle">It looks like this invitation has already been accepted.</Text>
            <Text variant="subtle">
              Please{' '}
              <KibbleLink as={Link} to="/login">
                log in
              </KibbleLink>{' '}
              to continue.
            </Text>
          </Flex>
        ) : (
          <Flex direction="column" gap={4}>
            <Flex direction="column" gap={1}>
              <Heading size="xl">Welcome to Otto</Heading>
              <Text variant="subtle" fontSize="sm">
                Please provide a few more details to complete your registration.
              </Text>
            </Flex>
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* TODO: Add prefix and suffix inputs */}
              {/* TODO: Add password and confirm password inputs */}
              <Stack gap={6}>
                <TextInput
                  label="Your First Name"
                  required
                  invalid={!!errors.firstName}
                  autoFocus
                  {...register('firstName', {
                    required: 'First name is required',
                  })}
                />
                <TextInput
                  label="Your Last Name"
                  required
                  invalid={!!errors.lastName}
                  {...register('lastName', {
                    required: 'Last name is required',
                  })}
                />
                <Button
                  type="submit"
                  size="lg"
                  fullWidth
                  loading={isAcceptingTeamInvitation || isFetchingInvitation}
                >
                  Join Team and Log In
                </Button>
              </Stack>
            </form>
          </Flex>
        )}
      </Card>
    </Flex>
  );
}
